import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import useAxiosAuth from "../hooks/useAxiosAuth"; // make sure path is correct

// Helper function to extract serializable error information
const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

// NOTE: admins live in their own `admins` table now (were in `doctors`). All
// admin reads/writes go through the /admins REST family. Endpoints return
// { success, data } for a single admin and { admins, total, … } for the list.

// Async thunk for uploading the admin profile image
export const AdminProfileUpdate = createAsyncThunk(
    'admin/profileUpdate',
    async (objdata, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(
                `/admins/${objdata.admId}/image`,
                objdata.formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            dispatch(getAdminById(objdata.admId))
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Async thunk for updating admin profile data
export const UpdateAdmin = createAsyncThunk(
    'admin/updateAdmin',
    async ({ admId, editedData }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/admins/${admId}`, editedData);
            dispatch(getAdminById(admId))
            return response.data?.data || response.data;
        } catch (error) {
            // `data` keeps the server's code + field (OTP_PROOF_REQUIRED,
            // FIELD_REQUIRED) for the profile form to put on the field.
            return rejectWithValue({ error: getSerializableError(error), data: error.response?.data || null });
        }
    }
);

// Async thunk for fetching the admin list (super_admin — Admins management page)
export const selectAdminList = createAsyncThunk(
    'admin/selectAdminList',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const { search = '', status = '', gender = [], countryId = [], tenantId = '', page = 1, limit = 10 } = typeof params === 'string' ? { search: params } : params;
            const qs = new URLSearchParams();
            if (search) qs.append('search', search);
            if (status) qs.append('status', status);
            if (gender?.length) qs.append('gender', gender.join(','));
            if (countryId?.length) qs.append('countryId', countryId.join(','));
            // Super Admin only — narrows the cross-tenant list to one hospital.
            if (tenantId) qs.append('tenantId', tenantId);
            qs.append('page', String(page));
            qs.append('limit', String(limit));
            const response = await axiosInstance.get(`/admins?${qs.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Async thunk for getting an admin by ID
export const getAdminById = createAsyncThunk(
    'admin/getAdminById',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/admins/${id}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Remove an admin's profile image (super_admin any · admin self)
export const removeAdminImage = createAsyncThunk(
    'admin/removeAdminImage',
    async (adminId, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`/admins/${adminId}/image`);
            dispatch(getAdminById(adminId));
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Create a new admin (super_admin)
export const createAdmin = createAsyncThunk(
    'admin/createAdmin',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`/admins`, payload);
            return response.data;
        } catch (error) {
            // Keep the raw body too: the login-policy 400s carry a machine
            // code + field that AddAdmin maps to a translated message.
            return rejectWithValue({ error: getSerializableError(error), data: error.response?.data || null });
        }
    }
);

// Delete an admin (super_admin)
export const deleteAdmin = createAsyncThunk(
    'admin/deleteAdmin',
    async (adminId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(`/admins/${adminId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Super_admin: reset a managed admin's password (nothing typed, nothing shown).
// Self-service change still goes through authSlice.userUpdatePassword.
export const resetAdminPassword = createAsyncThunk(
    'admin/resetAdminPassword',
    async ({ adminId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            // No password goes either way (owner, 2 Oct 2026) — the server
            // locks the current one and tells the admin to use Forgot password.
            const response = await axiosInstance.put(`/admins/${adminId}/password`, {});
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Toggle an admin's active/inactive status (super_admin)
export const setAdminStatus = createAsyncThunk(
    'admin/setAdminStatus',
    async ({ adminId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/admins/${adminId}/status`, { status });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const adminSlice = createSlice({
    name: "admin",
    initialState: {
        admin: null,
        adminList: [],
        adminTotal: 0,
        adminTotalPages: 1,
        adminListLoading: false,
        mutating: false,
        loading: false,
        error: null,
        success: false,
    },
    reducers: {
        resetAdminState: (state) => {
            state.admin = null;
            state.adminList = [];
            state.adminTotal = 0;
            state.adminTotalPages = 1;
            state.adminListLoading = false;
            state.mutating = false;
            state.loading = false;
            state.error = null;
            state.success = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // selectAdminList cases
            .addCase(selectAdminList.pending, (state) => {
                state.adminListLoading = true;
                state.error = null;
            })
            .addCase(selectAdminList.fulfilled, (state, action) => {
                state.adminListLoading = false;
                state.adminList = action.payload?.admins || [];
                state.adminTotal = action.payload?.total || 0;
                state.adminTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(selectAdminList.rejected, (state, action) => {
                state.adminListLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch admin list');
            })
            // AdminProfileUpdate cases
            .addCase(AdminProfileUpdate.pending, (state) => {
                state.loading = true;
                state.error = null;
                // state.success = false;
            })
            .addCase(AdminProfileUpdate.fulfilled, (state, action) => {
                state.loading = false;
                state.admin = action.payload;
                // state.success = true;
            })
            .addCase(AdminProfileUpdate.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Profile update failed');
                // state.success = false;
            })
            // UpdateAdmin cases
            .addCase(UpdateAdmin.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(UpdateAdmin.fulfilled, (state, action) => {
                state.loading = false;
                state.admin = action.payload;
                state.success = true;
            })
            .addCase(UpdateAdmin.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to update admin');
                state.success = false;
            })
            // getAdminById cases
            .addCase(getAdminById.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getAdminById.fulfilled, (state, action) => {
                state.loading = false;
                state.admin = action.payload;
                state.success = true;
            })
            .addCase(getAdminById.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Failed to fetch admin');
                state.success = false;
            })
            // create / delete / status — shared `mutating` flag; the page refetches on success.
            .addMatcher(
                (a) => [createAdmin.pending.type, deleteAdmin.pending.type, setAdminStatus.pending.type, resetAdminPassword.pending.type].includes(a.type),
                (state) => { state.mutating = true; state.error = null; }
            )
            .addMatcher(
                (a) => [createAdmin.fulfilled.type, deleteAdmin.fulfilled.type, setAdminStatus.fulfilled.type, resetAdminPassword.fulfilled.type].includes(a.type),
                (state) => { state.mutating = false; }
            )
            .addMatcher(
                (a) => [createAdmin.rejected.type, deleteAdmin.rejected.type, setAdminStatus.rejected.type, resetAdminPassword.rejected.type].includes(a.type),
                (state, action) => {
                    state.mutating = false;
                    state.error = typeof action.payload?.error === 'string' ? action.payload.error : (action.payload?.error?.message || 'Action failed');
                }
            );
    },
});

export const { resetAdminState } = adminSlice.actions;
export default adminSlice.reducer;